import { ArdoqId, ViewIds } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  updateViewContext as updateViewContextAction,
  updateViewProperties as updateViewPropertiesAction,
  updateViewSettings,
  type ViewContext,
} from '@ardoq/view-settings';
import { updateViewEmptyStateData as updateViewEmptyStateDataAction } from 'appLayout/ardoqStudio/emptyState/actions';
import { noop } from 'lodash';
import { componentGraphSelectionChanged as componentGraphSelectionChangedAction } from '@ardoq/component-selection';
import {
  graphViewLegendSetSelectedComponentTypes,
  graphViewLegendSetSelectedConditionalFormatting,
  graphViewLegendSetSelectedReferenceTypes,
} from 'tabview/graphViews/graphViewLegend/actions';
import {
  openDetailsDrawer as openDetailsDrawerAction,
  updateDetailsDrawer as updateDetailsDrawerAction,
} from 'appLayout/ardoqStudio/detailsDrawer/actions';
import {
  componentSelectedInPresentationMode,
  selectComponent as selectComponentAction,
} from 'streams/components/ComponentActions';
import { selectReference as selectReferenceAction } from 'streams/references/ReferenceActions';
import { componentSelectedInQuickExploreMode } from 'traverseOneLevel/actions';
import { trackNodeClickOnCanvas as trackNodeClickOnCanvasEvent } from './tracking';
import {
  type RelationshipsViewContextMenuRequest,
  relationshipsViewContextMenuRequested,
} from './actions';
import type {
  RelationshipsViewCommands,
  RelationshipsViewDependencies,
} from './types';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;

const resetLegendSelection = () => {
  dispatchAction(
    graphViewLegendSetSelectedComponentTypes({ componentTypes: [] })
  );
  dispatchAction(
    graphViewLegendSetSelectedReferenceTypes({ referenceTypes: [] })
  );
  dispatchAction(graphViewLegendSetSelectedConditionalFormatting({ ids: [] }));
};

const setGraphSelection = (componentIds: ArdoqId[]) =>
  dispatchAction(
    componentGraphSelectionChangedAction({ graphSelection: componentIds })
  );

const updateDetailsDrawer = (componentId: ArdoqId) =>
  dispatchAction(updateDetailsDrawerAction([componentId]));

const openDetailsDrawer = (componentId: ArdoqId) =>
  dispatchAction(openDetailsDrawerAction([componentId]));

const selectComponentInPresentationMode = (componentId: ArdoqId) =>
  dispatchAction(componentSelectedInPresentationMode({ id: componentId }));

const selectComponentInQuickExploreMode = (componentId: ArdoqId) =>
  dispatchAction(componentSelectedInQuickExploreMode(componentId));

const selectComponent = (componentId: ArdoqId) =>
  dispatchAction(selectComponentAction({ cid: componentId }));

const selectReference = (referenceId: ArdoqId) =>
  dispatchAction(selectReferenceAction({ cid: referenceId }));

const requestContextMenu = (request: RelationshipsViewContextMenuRequest) =>
  dispatchAction(relationshipsViewContextMenuRequested(request));

const setCollapsedGroupIds = (collapsedGroupIds: string[]) =>
  dispatchAction(
    updateViewSettings({
      viewId: VIEW_ID,
      settings: { collapsedGroupIds },
      persistent: true,
    })
  );

const trackNodeClickOnCanvas = (nodeType: 'group' | 'component') =>
  trackNodeClickOnCanvasEvent({ viewId: VIEW_ID, nodeType });

const updateViewContext = (viewContext: ViewContext) =>
  dispatchAction(updateViewContextAction(viewContext));

const updateViewProperties = (viewProperties: RelationshipsViewDependencies) =>
  dispatchAction(
    updateViewPropertiesAction({ viewId: VIEW_ID, viewProperties })
  );

const updateViewEmptyStateData = (isViewEmpty: boolean) =>
  dispatchAction(
    updateViewEmptyStateDataAction({
      viewEmptyStateData: { isViewEmpty },
      viewId: VIEW_ID,
    })
  );

export type RelationshipsViewCommandsConfig = {
  isPresentationMode: boolean;
  isQuickExploreSelectionEnabled: boolean;
};

export const getSelectComponentInViewpointMode = ({
  isPresentationMode,
  isQuickExploreSelectionEnabled,
}: RelationshipsViewCommandsConfig): ((componentId: ArdoqId) => void) => {
  if (isPresentationMode) {
    return selectComponentInPresentationMode;
  }
  if (isQuickExploreSelectionEnabled) {
    return selectComponentInQuickExploreMode;
  }
  return openDetailsDrawer;
};

export const createRelationshipsViewCommands = (
  config: RelationshipsViewCommandsConfig
): RelationshipsViewCommands => ({
  resetLegendSelection,
  setGraphSelection: config.isQuickExploreSelectionEnabled
    ? setGraphSelection
    : noop,
  updateDetailsDrawer,
  selectComponentInViewpointMode: getSelectComponentInViewpointMode(config),
  selectComponent,
  selectReference,
  requestContextMenu,
  setCollapsedGroupIds,
  trackNodeClickOnCanvas,
  updateViewContext,
  updateViewProperties,
  updateViewEmptyStateData,
});

export const NOOP_RELATIONSHIPS_VIEW_COMMANDS: RelationshipsViewCommands = {
  resetLegendSelection: noop,
  setGraphSelection: noop,
  updateDetailsDrawer: noop,
  selectComponentInViewpointMode: noop,
  selectComponent: noop,
  selectReference: noop,
  requestContextMenu: noop,
  setCollapsedGroupIds: noop,
  trackNodeClickOnCanvas: noop,
  updateViewContext: noop,
  updateViewProperties: noop,
  updateViewEmptyStateData: noop,
};
